import { convertToRaw, EditorState, SelectionState, type RawDraftContentState } from 'draft-js';
import { EDITOR_STATE } from 'handsontable/editors/baseEditor';

import rawGetText from '../../../../common/utils/editor/rawUtils/rawGetText';
import { asRect } from '../../../../common/maths/geometry/rect';
import type { MilanoteEditorRef } from '../../../components/editor/milanoteEditor/MilanoteEditorTypes';
import forceFocus from '../../../components/editor/customRichUtils/forceFocus';
import replaceSelectionWithText from '../../../components/editor/customRichUtils/replaceSelectionWithText';
import MilanoteCellEditor, { type InsertPlainTextAtSelectionOptions } from './MilanoteCellEditor';
import { KEY_CODES } from '../../../utils/keyboard/keyConstants';
import type { Range } from '@tiptap/core';
import { isSelectingSingleSelectionAndCell } from '../utils/selectionUtils/tableSelectionPredicateUtils';

type MilanoteEditorRefDraft = MilanoteEditorRef<EditorState> & {
    editor: {
        editor: {
            _onKeyDown: (event: KeyboardEvent) => void;
            editor: HTMLElement;
        };
    };
};

/**
 * Converts a pointer event into the rect used to scroll the editor into view.
 */
const getEditorFocusRect = (event?: Event) => {
    if (!(event instanceof MouseEvent)) return;

    return asRect({ x: event.clientX, y: event.clientY, width: 0, height: 0 });
};

export default class DraftCellEditor extends MilanoteCellEditor<EditorState, RawDraftContentState> {
    /**
     * Starts the legacy Draft editor immediately after its React session is created.
     */
    beginEditing(overwriteText?: string, event?: Event): void {
        if (
            !isSelectingSingleSelectionAndCell(this.milanoteHot.getSelected()) ||
            this.milanoteHot.milanoteProps.isResizing ||
            this.state !== EDITOR_STATE.VIRGIN
        )
            return;

        this.onEditorBeginEditing(overwriteText, event);
        this.milanoteHot.milanoteProps.setCellEditingSession({
            sessionId: ++this.cellEditingSessionId,
            cellCoords: { row: this.row, col: this.col },
            overwriteText,
            event,
        });

        const editorFocusRect = getEditorFocusRect(event);

        void this.milanoteHot.milanoteProps.startEditingCellEditor(editorFocusRect);

        super.beginEditing(overwriteText, event);
    }

    getEditorInstance(): MilanoteEditorRefDraft | null {
        const { cellEditorRef } = this.milanoteHot.milanoteProps;
        return (cellEditorRef?.current as MilanoteEditorRefDraft | null) || null;
    }

    onEditorBeginEditing(_overwriteText?: string, event?: Event): void {
        const cellEditor = this.getEditorInstance();

        if (!cellEditor) return;

        // Trigger compositionstart event to make sure that the editor is in composition mode.
        if (event && event.type === 'compositionstart') {
            const eventClone = new CompositionEvent(event.type, event as CompositionEventInit);
            cellEditor.editor.editor.editor.dispatchEvent(eventClone);
        }
    }

    onEditorChange(editorState: EditorState): void {
        this.content = convertToRaw(editorState.getCurrentContent());
    }

    getEditorValue(): string {
        return rawGetText(this.content);
    }

    onEditorFinishEditing(): void {
        const cellEditor = this.getEditorInstance();

        if (!cellEditor) return;

        const editorState = cellEditor.getEditorState();

        // If the editor is in composition mode, we need to synchronously get it out of composition mode so
        //  the editor can be updated prior to "finishEditing" being called on the cell.
        // To exit composition mode, we need to simulate a key press, otherwise the editor will wait for
        //  a 20ms delay before exiting the composition mode, by using the right arrow key, the event will
        //  have its default prevented by the DraftEditorCompositionHandler.
        // Once this composition mode is exited, the MilanoteEditor will save the editor state to this.content
        //  via the "this.setEditorState" in the saveData, which is called from the onChange.
        if (editorState && editorState.isInCompositionMode()) {
            const keyDownEvent = new KeyboardEvent('keydown', { key: 'ArrowRight', which: KEY_CODES.RIGHT_ARROW });
            cellEditor.editor.editor._onKeyDown(keyDownEvent);
            return;
        }

        // Just before the user finish editing, get the last updated editor state and save it to this.content,
        // this is to make sure that the latest changes are saved.
        this.onEditorChange(editorState);
    }

    clearEditorContent(): void {
        this.content = null;
    }

    setPlainTextContent(plainText: string, style?: string[]): void {
        const cellEditor = this.getEditorInstance();

        if (!cellEditor) return;

        cellEditor.setPlainText(plainText, style);
    }

    getPlainTextContent(): string {
        const cellEditor = this.getEditorInstance();

        if (!cellEditor) return '';

        return cellEditor.getEditorState().getCurrentContent().getPlainText();
    }

    /**
     * Inserts plain text at the active selection and can also replace characters immediately before it.
     */
    insertPlainTextAtSelection(plainText: string, options?: InsertPlainTextAtSelectionOptions): void {
        const cellEditor = this.getEditorInstance();
        if (!cellEditor) return;

        const editorState = cellEditor.getEditorState();
        const selection = editorState.getSelection();
        const replacePrevCharacters = Math.max(options?.replacePrevCharacters || 0, 0);
        const replacementSelection = SelectionState.createEmpty(selection.getStartKey()).merge({
            anchorKey: selection.getStartKey(),
            anchorOffset: Math.max(selection.getStartOffset() - replacePrevCharacters, 0),
            focusKey: selection.getEndKey(),
            focusOffset: selection.getEndOffset(),
            isBackward: false,
            hasFocus: true,
        });

        const newEditorState = replaceSelectionWithText(plainText)(forceFocus(editorState, replacementSelection));

        cellEditor.onChange(newEditorState);
        this.onEditorChange(newEditorState);
    }

    getPlainTextSelection(): Range | null {
        const selection = this.getEditorInstance()?.getEditorState().getSelection();
        if (!selection) return null;

        return {
            from: selection.getStartOffset(),
            to: selection.getEndOffset(),
        };
    }

    focus(): void {
        this.getEditorInstance()?.getEditor()?.focus?.();
    }

    hasValidElement(): boolean {
        const cellEditor = this.getEditorInstance();
        return !!cellEditor;
    }

    setEditorState(newEditorState: EditorState): void {
        const cellEditor = this.getEditorInstance();
        if (!cellEditor) return;
        cellEditor.onChange(newEditorState);
    }

    getEditorState(): EditorState {
        const cellEditor = this.getEditorInstance();
        if (!cellEditor) return EditorState.createEmpty();
        return cellEditor.getEditorState();
    }
}
